import { ButtonGroup, Button, Typography } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import dayjs from 'dayjs';
import CustomTimeZoneDateRangePicker from 'components/DatePIcker/CustomTimeZoneDateRangePicker';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  ignoreYtd?: boolean;
  isSelected: string;
  setIsSelected: any;
  handleDateChange: any;
  startDate: any;
  setStartDate: any;
  endDate: any;
  setEndDate: any;
  yesterday: any;
  isLoading: boolean;
  isTimeline?: boolean;
  isMaxEndDate?: boolean;
}

const DateRange = ({
  enableDarkTheme,
  isSelected,
  setIsSelected,
  handleDateChange,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  yesterday,
  isLoading,
  isTimeline,
  isMaxEndDate = true,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const handleSelect = (id: string) => {
    setIsSelected(id);
    const today = dayjs();
    let newStartDate = startDate;
    let newEndDate = yesterday;

    switch (id) {
      case 'Today': {
        newStartDate = today.toDate();
        newEndDate = today.toDate();
        break;
      }
      case '1D':
        newStartDate = dayjs(yesterday).subtract(1, 'day').toDate();
        newEndDate = yesterday;
        break;
      case '7D':
        newStartDate = dayjs(yesterday).subtract(7, 'days').toDate();
        newEndDate = yesterday;
        break;
      case '14D':
        newStartDate = dayjs(yesterday).subtract(14, 'days').toDate();
        newEndDate = yesterday;
        break;
      case '30D':
        newStartDate = dayjs(yesterday).subtract(30, 'days').toDate();
        newEndDate = yesterday;
        break;
      case 'Custom':
        // Do nothing, user will select manually
        break;
      default:
        break;
    }

    if (id !== 'Custom') {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
    }
  };

  const dateRangeList = ['Today', '1D', '7D', '14D', '30D', 'Custom'];

  const dateRangeLabel = (item: string): string => {
    const map: Record<string, string> = {
      Today: t('date_range_today'),
      '1D': t('date_range_1d'),
      '7D': t('date_range_7d'),
      '14D': t('date_range_14d'),
      '30D': t('date_range_30d'),
      Custom: t('date_range_custom'),
    };
    return map[item] ?? item;
  };

  return (
    <>
      <Typography
        style={{ fontFamily: "'Inter Variable', sans-serif", margin: '20px 0' }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              color: enableDarkTheme
                ? ThemePalette.typography.mediumGray
                : '#3d3d3d',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Typography style={{ fontSize: '12px', marginRight: '10px' }}>
              {t('quick_range_10')}
            </Typography>
            <ButtonGroup
              size="small"
              variant="outlined"
              aria-label={t('basic_button_group')}
              // sx={{
              //   border: '1px solid #2F445D',
              // }}
              disabled={isLoading}
            >
              {dateRangeList.map((item) => {
                return (
                  <Button
                    key={item}
                    size="small"
                    disabled={
                      // loadingStates?.isLoadingFleetSummary ||
                      isTimeline && item !== '1D' && item !== 'Custom'
                    }
                    style={{
                      backgroundColor:
                        isSelected === item
                          ? palette.primary
                          : isTimeline && item !== '1D' && item !== 'Custom'
                          ? palette.borderGrey
                          : enableDarkTheme
                          ? palette.boxBackground
                          : palette.toolBarBackground,
                      color:
                        isTimeline && item !== '1D' && item !== 'Custom'
                          ? ThemePalette.typography.grey
                          : isSelected === item
                          ? ThemePalette.typography.white
                          : palette.textPrimary,
                    }}
                    onClick={() => {
                      handleSelect(item);
                    }}
                  >
                    {dateRangeLabel(item)}
                  </Button>
                );
              })}
            </ButtonGroup>
          </div>
          {isSelected === 'Custom' && (
            <div
              style={{
                color: enableDarkTheme
                  ? ThemePalette.typography.mediumGray
                  : '#3d3d3d',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <Typography style={{ fontSize: '12px', margin: '0 10px 0 50px' }}>
                {t('date_range_10')}
              </Typography>
              <CustomTimeZoneDateRangePicker
                startDate={startDate}
                endDate={endDate}
                onFinalDateChange={handleDateChange}
                maxDate={dayjs(new Date())}
                // maxDate={dayjs(yesterday)}
                maxDaysFromStartDate={isMaxEndDate ? 30 : undefined}
                sx={{
                  minWidth: '148px',
                  maxWidth: '148px',
                  width: '148px',
                }}
                disabled={isSelected !== 'Custom'}
              />
            </div>
          )}
        </div>
      </Typography>
    </>
  );
};

export default DateRange;
